import * as React from 'react'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { EntityDto } from '../../config/appConfig'
import BaseOverview, { BaseOverviewCount, BaseOverviewProps } from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import HammockFormDialog, { HammockFormDialogProps } from '../../v2/components/Dialog/HammockFormDialog'
import { FormikConfig } from 'formik/dist/types'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { generatePath } from 'react-router'

/**
 * Basis for overview page props. As little as possible is required.
 */
export type BaseMutableOverviewProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DialogBodyEntityT = any,
    DialogBodyPropsT extends BaseMutableOverviewDialogProps<EntityT> = BaseMutableOverviewDialogProps<EntityT>,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonPropsT> & {
    mutationDialogProps?: Omit<HammockFormDialogProps<DialogBodyEntityT, DialogBodyPropsT>, 'dialogBody' | 'dialogFormBody'>
    mutationDialogFormProps?: (item: EntityT) => FormikConfig<DialogBodyEntityT>
    mutationDialogBody: FunctionComponent<HammockFormDialogProps<DialogBodyEntityT, DialogBodyPropsT>>
    mutationDialogBodyProps?: (item: EntityT) => DialogBodyPropsT
    openMutationDialog?: boolean
    setOpenMutationDialog?: (open: boolean) => void
}

export type MutationRouteMatchProps = { action?: MutationAction }

export enum MutationAction {
    ADD = 'add',
    EDIT = 'edit',
}

export type BaseMutableOverviewDialogProps<EntityT extends EntityDto> = {
    id?: string
    item?: EntityT
}

export type BaseMutableOverviewActionButtonProps<EntityT> = {
    openMutationDialog?: boolean
    setOpenMutationDialog?: (open: boolean, item?: EntityT) => void
}

/**
 * This Base Overview adds a HammockFormDialog to the BaseOverview component to (for example) allow items in the list to be edited or items to be added to the list.
 *
 * @param props see {@link BaseMutableOverviewProps} for more information on which fields to include.
 */
export default function BaseMutableOverview<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonProps = BaseMutableOverviewActionButtonProps<EntityT>,
    DefaultFilterTagsPropsT = object,
    DialogBodyEntityT = object,
    DialogBodyPropsT extends BaseMutableOverviewDialogProps<EntityT> = BaseMutableOverviewDialogProps<EntityT>,
>(props: BaseMutableOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonProps, DialogBodyEntityT, DialogBodyPropsT>) {
    const history = useHistory()
    const pathParams = useParams<MutationRouteMatchProps>()
    const { path: currentPath, params: routeParams } = useRouteMatch<MutationRouteMatchProps>()

    const [clickedItem, setClickedItem] = useState<EntityT>()
    const [_openMutationDialog, _setOpenMutationDialog] = useState(false)

    const openMutationDialog = useMemo(() => props.openMutationDialog ?? _openMutationDialog, [props.openMutationDialog, _openMutationDialog])
    const setOpenMutationDialog = (open: boolean) => {
        if (props.setOpenMutationDialog) {
            props.setOpenMutationDialog(open)
        }
        _setOpenMutationDialog(open)
    }

    useEffect(() => setOpenMutationDialog(routeParams.action === MutationAction.ADD), [routeParams])

    const [reloadTrigger, setReloadTrigger] = useState<number>(0)
    const reload = () => {
        setReloadTrigger(reloadTrigger + 1)
    }

    return (
        <BaseOverview<EntityT, FilterT, SortingT, HeaderT, ActionButtonProps, DefaultFilterTagsPropsT>
            onItemClicked={itemProps => {
                props.onItemClicked?.(itemProps)
                setClickedItem(itemProps.item)
                setOpenMutationDialog(true)
            }}
            {...props}
            headerActionsButtonsProps={{
                ...props.headerActionsButtonsProps,
                openMutationDialog,
                setOpenMutationDialog: (open: boolean, item?: EntityT) => {
                    if (item !== undefined) {
                        setClickedItem(item)
                    }
                    setOpenMutationDialog(open)
                },
            }}
            reloadTrigger={reloadTrigger}
            focusQueryOnRender={props.focusQueryOnRender !== true && !routeParams?.action}
        >
            <HammockFormDialog<Partial<DialogBodyEntityT>, DialogBodyPropsT>
                open={openMutationDialog}
                setOpen={setOpenMutationDialog}
                {...props.mutationDialogProps}
                dialogFormProps={() => props.mutationDialogFormProps(clickedItem)}
                dialogFormBody={props.mutationDialogBody}
                dialogFormBodyProps={() => ({ id: clickedItem?.id, ...props.mutationDialogBodyProps?.(clickedItem) })}
                onDismiss={() => {
                    setOpenMutationDialog(false)
                    history.push(generatePath(currentPath, { ...pathParams, action: undefined }))
                    reload()
                }}
            />
        </BaseOverview>
    )
}
